import { Injectable } from '@angular/core'

import { set as _set } from 'lodash/fp'
import Papa from 'papaparse'

import { Observable, from } from 'rxjs'

import {
  ContentValue,
  Enumeration,
  EnumerationDescription,
} from '@flowx/designer/shared/entities/enumeration.entity'

@Injectable({
  providedIn: 'root',
})
export class EnumerationsCsvService {
  // IMPORT
  public parseEnumerationsCSV = (file: File): Observable<Enumeration[]> =>
    from(
      // eslint-disable-next-line no-async-promise-executor
      new Promise<Enumeration[]>(async (resolve, reject) => {
        Papa.parse(file, {
          complete: ({ data }: { data: string[][] }) => {
            const [header, ...values] = data
            // TODO: check csv results - parse/incorrectness errors go here
            resolve(this.getEnumerationContents(header, values))
          },
          error: (err: any) => reject(err), // only FileReader related errors
        })
      })
    )

  // EXPORT
  public transformJsonEnumerationCSV = async (
    json: Enumeration[],
    languages: string[],
    sourceSystems: string[]
  ): Promise<string> => {
    // get headers
    const headers = [
      // description
      'description.name',
      'description.type',
      // values
      'contentValue.type',
      'contentValue.order',
      'contentValue.code',
      ...languages.map((key) => `contentValue.contentMap.${key}`),
      ...sourceSystems.map((key) => `contentValue.sourceSystems.${key}`),
      'contentValue.childContentDescription.name',
    ]

    // get entries
    const entries = this.getEntriesRecursive(json, languages, sourceSystems)
    return Papa.unparse([headers, ...entries])
  }

  getEntriesRecursive(
    contents: Enumeration[],
    languages: string[],
    sourceSystems: string[]
  ): any[] {
    let entries = []
    for (const entry of contents) {
      const description = [entry.description.name, entry.description.type]

      entry.contentValues.forEach((contentValue) => {
        entries.push([
          ...description,
          contentValue.type,
          contentValue.order,
          contentValue.code,
          ...languages.map((key) => contentValue.contentMap[key] || ''),
          ...sourceSystems.map((key) =>
            contentValue.sourceSystems ? contentValue.sourceSystems[key] : ''
          ),
          contentValue.childContentDescription?.name || '',
        ])

        if (contentValue.childContent) {
          entries = [
            ...entries,
            ...this.getEntriesRecursive([contentValue.childContent], languages, sourceSystems),
          ]
        }
      })
    }

    return entries
  }

  private getEnumerationContents = (paths: string[], values: string[][]): Enumeration[] => {
    const descriptionNameIndex = paths.indexOf('description.name')
    const rows = values.filter(this.hasValueForIndex(descriptionNameIndex))

    // The CSV is a depth-first serialization: a value's child rows immediately follow it,
    // scoped to that occurrence. Rebuild the tree by walking rows in order so each occurrence
    // owns its own children. Matching by name across the whole file (the previous approach)
    // merged same-named children from different roots — duplicating codes and dropping subtrees.
    const cursor = { index: 0 }
    const peekName = (): string | undefined => rows[cursor.index]?.[descriptionNameIndex]

    const buildEnum = (name: string): Enumeration => {
      const enumeration: Enumeration = {
        description: this.extractDescription(paths, rows[cursor.index]),
        contentValues: [],
      }

      while (cursor.index < rows.length && peekName() === name) {
        const row = rows[cursor.index++]
        const contentValue = this.getValuesWithPath<ContentValue>(paths, row, 'contentValue')
        const childName = contentValue.childContentDescription?.name

        const value = this.updateChildContentDescription(contentValue)
        if (childName) {
          const child = buildEnum(childName)
          value.childContent = {
            description: child.description,
            contentValues: child.contentValues,
          }
        }

        enumeration.contentValues.push(value)
      }

      return enumeration
    }

    const enums: Enumeration[] = []
    while (cursor.index < rows.length) {
      enums.push(buildEnum(peekName()))
    }

    return enums
  }

  private hasValueForIndex = (index: number) => (val: string[]) => !!val[index]

  private extractDescription = (paths: string[], row: string[]): EnumerationDescription => {
    const description = this.getValuesWithPath<EnumerationDescription>(paths, row, 'description')

    // 4.1 compatibility fix
    if (description.type === 'enumerations') {
      description.type = 'ENUMERATION'
    } else if (description.type === 'substitutionTags') {
      description.type = 'SUBSTITUTION_TAG'
    }

    return description
  }

  private getValuesWithPath = <T>(
    paths: string[],
    currentEnumValue: string[],
    pathPrefix: string
  ): T =>
    paths.reduce((contentValue: T, path: string, index: number) => {
      if (path.startsWith(pathPrefix)) {
        const objectPath = path.split(`${pathPrefix}.`)[1]
        const value = this.updateValue(currentEnumValue[index], path)
        contentValue = _set<T>(objectPath, value, contentValue as object)
      }
      return contentValue
    }, {} as T)

  private updateValue = (value: any, path: string): any => {
    // TODO: check if there are any more specific rules
    if (value === '') {
      return null
    }

    if (typeof value === 'object' && value.name === null) {
      return null
    }

    switch (path) {
      case 'description.version':
      case 'contentValue.order':
        return parseInt(value, 10)
    }

    return value
  }

  private updateChildContentDescription = (childContent: ContentValue): ContentValue => {
    if (!childContent.childContentDescription?.name) {
      return {
        ...childContent,
        childContentDescription: null,
      }
    }
    return childContent
  }
}
